import React, { useEffect, useState } from 'react'
import { Box, Button, Grid, makeStyles } from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Formik, Form } from 'formik'
import CustomBox from 'components/Form/CustomBox'
import ViewHeader from 'components/ViewHeader'
import ProgressButton from 'components/ProgressButton'
import { addVendor, fetchVendorById, resetVendor, updateVendorById } from 'store/vendor'
import ActionBar from 'components/ActionBar'
import CustomTextField from 'components/Form/CustomTextField'
import { vendorFormSchema } from 'schemas/vendor'
import { Collection, EXHIBITOR_INFO_TEXT } from 'constants/index'
import { submitSubjectData } from 'store/subject'
import AvatarPicker from 'components/AvatarPicker'
import { uploadImageFirstTime } from 'utils'
import { omit } from 'lodash'
import InfoIconAlongText from 'components/InfoIconAlongText'
import ReactQuill from 'react-quill'
import { modules } from 'views/admin/settings/SettingForm/TemplateForm'
import ErrorMessage from 'components/ErrorMessage'

const VendorForm = () => {
  const classes = useStyles()
  const { vendorId } = useParams()
  const dispatch = useDispatch()
  const history = useHistory()
  const { user } = useSelector((state) => state.auth)
  const { vendor } = useSelector((state) => state.vendor)
  const [logoFile, setLogoFile] = useState(null)
  const [loading, setLoading] = useState(false)

  const handleImageChange = (file) => {
    setLogoFile(file)
  }

  const initialValues = {
    email: vendor?.email ?? '',
    name: vendor?.name ?? '',
    booth: vendor?.booth ?? '',
    websiteUrl: vendor?.websiteUrl ?? '',
    intro: vendor?.intro ?? '',
    message: vendor?.message ?? '',
    logoImage: vendor?.logoImage ?? ''
  }

  const handleLogoUpload = async (name) => {
    const modifiedVendor = omit(vendor, 'id')
    await dispatch(
      updateVendorById(vendorId, { ...modifiedVendor, logoImage: name }, user?.conferenceId)
    )
  }

  const handleLogoDelete = async () => {
    const modifiedVendor = omit(vendor, 'id')
    await dispatch(
      updateVendorById(vendorId, { ...modifiedVendor, logoImage: null }, user?.conferenceId)
    )
  }

  const goBack = () => {
    dispatch(resetVendor())
    history.goBack()
  }

  useEffect(() => {
    if (vendorId) {
      dispatch(fetchVendorById(vendorId))
    }
    return () => dispatch(resetVendor())
  }, [vendorId])

  const onSubmit = async (values) => {
    setLoading(true)
    const payload = {
      email: values.email.trim(),
      name: values.name.trim(),
      booth: values.booth.trim(),
      websiteUrl: values.websiteUrl.trim(),
      intro: values.intro.trim(),
      message: values.message.trim()
    }
    if (logoFile) {
      payload.logoImage = await uploadImageFirstTime(
        logoFile,
        Collection.VENDORS,
        user?.conferenceId
      )
    }

    try {
      if (vendorId) {
        const res = await dispatch(updateVendorById(vendorId, payload, user?.conferenceId))
        vendor?.subjectId && (await dispatch(submitSubjectData(vendor?.subjectId, payload)))
        res && toast.success('Exhibitor updated successfully')
      } else {
        const vendorId = await dispatch(addVendor(payload, user?.conferenceId))
        vendorId && toast.success('Exhibitor added successfully.')
      }
      setLoading(false)
      goBack()
    } catch (err) {
      // do nothing
    }
  }

  return (
    <CustomBox>
      <ViewHeader title={vendorId ? 'Edit Exhibitor' : 'Create Exhibitor'}>
        <ActionBar actionText="Back" onAction={goBack} />
      </ViewHeader>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={onSubmit}
        validationSchema={vendorFormSchema}>
        {({ values, touched, errors, setFieldValue }) => (
          <Form>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <CustomTextField label="Email" name="email" type="email" disabled={!!vendorId} />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomTextField label="Exhibitor Name" name="name" type="text" />
              </Grid>
              <Grid item xs={12} md={4}>
                <CustomTextField label="Booth No" name="booth" type="text" />
              </Grid>
              <Grid item xs={12} md={8}>
                <CustomTextField label="Website URL" name="websiteUrl" type="text" />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <label className={classes.label}>
                    Introduction
                    <InfoIconAlongText infoText={EXHIBITOR_INFO_TEXT.introduction} />
                  </label>
                  <ReactQuill
                    theme="snow"
                    modules={modules}
                    value={values.intro}
                    onChange={(value) => setFieldValue('intro', value)}
                    placeholder="Write introduction here..."
                  />
                  <ErrorMessage message={touched?.intro && errors?.intro} />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <label className={classes.label}>
                    Message
                    <InfoIconAlongText infoText={EXHIBITOR_INFO_TEXT.message} />
                  </label>
                  <ReactQuill
                    theme="snow"
                    modules={modules}
                    value={values.message}
                    onChange={(value) => setFieldValue('message', value)}
                    placeholder="Write message here..."
                  />
                  <ErrorMessage message={touched?.message && errors?.message} />
                </Box>
              </Grid>

              <Grid item xs={12} lg={6}>
                <AvatarPicker
                  title="Exhibitor Logo"
                  subTitle="Allowed Format: png/jpg ; Dimensions : 320x320"
                  path={Collection.VENDORS}
                  handleCustomChange={handleImageChange}
                  onUpload={handleLogoUpload}
                  onDelete={handleLogoDelete}
                  imageName={vendorId ? vendor?.logoImage : null}
                  resetImage={!(vendorId && vendor?.logoImage)}
                  firsTimeUpload={!vendorId}
                  conferenceId={user?.conferenceId}
                  infoText={EXHIBITOR_INFO_TEXT.logo}
                />
              </Grid>
            </Grid>
            <Grid container justifyContent="flex-end" spacing={2}>
              <Grid item>
                <Button disabled={loading} size="large" onClick={goBack}>
                  Cancel
                </Button>
              </Grid>
              <Grid item>
                <ProgressButton
                  type="submit"
                  label="Save"
                  inProgress={loading}
                  disabled={loading}
                />
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </CustomBox>
  )
}
export default VendorForm

const useStyles = makeStyles({
  label: {
    fontSize: '13px',
    letterSpacing: '0.5px',
    color: '#606060',
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    marginLeft: '0.2rem'
  }
})
